export type AncCellRendererFn = (value: unknown, method: string, row: unknown) => string | null;

export type AncCellRendererMethod = (value: unknown, row: unknown) => string | null | undefined;

const PRINT_STATUS_YES = 'yes';

function getAttrs(row: unknown): Record<string, unknown> {
    const r = row as { attributes?: Record<string, unknown> } | null | undefined;
    return (r?.attributes ?? r ?? {}) as Record<string, unknown>;
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function escapeAttr(text: string): string {
    return escapeHtml(text).replace(/'/g, '&#39;');
}

/** Legacy ngx-grid copy cell: text + icon with data-anc-copy for grid delegation. */
function renderCopyCell(value: unknown): string {
    if (value == null || value === '') return '-';
    const text = String(value);
    return (
        `<span class="anc-copy-cell">` +
        `<span class="anc-copy-cell-text">${escapeHtml(text)}</span>` +
        `<button type="button" class="anc-copy-trigger icon icon-copy effect ml-1 pt-1" ` +
        `data-anc-copy="${escapeAttr(text)}" aria-label="Copy to clipboard"></button>` +
        `</span>`
    );
}

function formatMomentDate(date: unknown): string {
    if (!date || date === '-') return '-';
    const d = new Date(date as string);
    if (Number.isNaN(d.getTime())) return '-';
    const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = d.getDate();
    const year = d.getFullYear();
    if (!hasTime) return `${month} ${day}, ${year}`;
    const time = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${month} ${day}, ${year}, ${time}`;
}

function formatYearDate(date: unknown): string {
    if (!date) return '-';
    const d = new Date(date as string);
    if (Number.isNaN(d.getTime())) return '-';
    const month = d.toLocaleString('en-US', { month: 'short' });
    return `${month} ${d.getDate()}, ${d.getFullYear()}`;
}

function formatSimpleDate(date: unknown): string {
    if (date) {
        const d = new Date(date as string);
        if (Number.isNaN(d.getTime())) return '-';
        const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
        let formatted = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        if (hasTime) {
            const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
            formatted += `, ${timeStr}`;
        }
        return formatted;
    }
    if (date === null) return '<div class="dash-center"> - </div>';
    return '-';
}

function modifyDisplayNameForGrowMorelogistic(name: unknown): string {
    if (name == null || name === '') return '-';
    const str = String(name);
    if (typeof localStorage === 'undefined') return str;

    const isGrowmoreLogistic = localStorage.getItem('isGrowmoreLogistic');
    const isDpdLogistic = localStorage.getItem('isDpdLogistic');
    const displayNameMap: Record<string, { growmore: string; default: string }> = {
        'E-PAQ Standard': { growmore: 'Standard', default: 'DPD Classic' },
        'E-PAQ Select': { growmore: 'Customer Pickup', default: 'DPD Predict' },
        'E-PAQ Plus': { growmore: 'Express', default: 'DPD Medical' },
        'E-PAQ Elite': { growmore: '', default: 'DPD Relais Pickup' }
    };
    const mapping = displayNameMap[str];
    if (!mapping || (!isGrowmoreLogistic && !isDpdLogistic)) return str;
    return isGrowmoreLogistic ? mapping.growmore : mapping.default;
}

const DEFAULT_RENDERERS: Record<string, AncCellRendererMethod> = {
    formatMomentDate: (value) => formatMomentDate(value),
    formatSimpleDate: (value) => formatSimpleDate(value),
    formatYearDate: (value) => formatYearDate(value),

    parseCheckNull: (value) => (value ? String(value) : '-'),
    parseNullableNumber: (value) => (value === null || value === undefined ? '-' : String(value)),
    parseUnsyncMessage: (value) => (value != null && value !== '' ? String(value) : '-'),

    parseOrderStatus: (value) => {
        if (!value) return '-';
        const str = String(value);
        const lowerValue = str.split(' ').join('-').toLowerCase();
        return `<div class="order-block"><div class="status-title"><span class="title-${lowerValue}">${str}</span></div></div>`;
    },

    parseOrderPdtUnits: (value) =>
        value
            ? `<span class="anc-units-cell"><span class="anc-units-cell__value">${escapeHtml(String(value))}</span><span class="icon-green-circle" aria-hidden="true"></span></span>`
            : '-',

    parsePrintStatus: (value) => {
        if (value === PRINT_STATUS_YES) {
            return `<div class="print-status-block"><div class="status-title"><span class="print-text">Printed</span><span class="print-img icon icon-check-circle"></span></div></div>`;
        }
        return `<div class="print-status-block"><div class="status-title"><span>Not Printed</span><span class="print-img icon icon-printer"></span></div></div>`;
    },

    parseWarehouseCode: (_value, row) => {
        const attrs = getAttrs(row);
        return attrs.warehouse_code != null && attrs.warehouse_code !== '' ? String(attrs.warehouse_code) : '-';
    },

    parseNameModify: (value) => modifyDisplayNameForGrowMorelogistic(value),

    parseWMSStatus: (value) => {
        if (!value) return '-';
        const successCodes = [2, 5, 7, 12, 13];
        const iconClass = successCodes.includes(Number(value))
            ? 'icon-Success-grid-icon'
            : 'icon-Error-grid-icon';
        return `<span class="icon title-icon ${iconClass}"></span>`;
    },

    parseCheckPostcode: (value, row) => {
        if (!value) return '-';
        const attrs = getAttrs(row);
        if (attrs.is_northern_ireland) {
            return `${value}<span class="ml-3 icon-red icon-north-ireland"></span>`;
        }
        return String(value);
    },

    parsePOPdt: (value) => {
        const data = value as { received_quantity?: number; quantity?: number } | null;
        return data ? `${data.received_quantity}/${data.quantity}` : '-';
    },

    parseComplianceCode: (value) => {
        if (value === true) {
            return `<div class="po-block"><div class="status-title"><span class="icon title-icon icon-Error-grid-icon"></span></div></div>`;
        }
        if (value === false) {
            return `<div class="po-block"><div class="status-title"><span class="icon title-icon icon-Success-grid-icon"></span></div></div>`;
        }
        return '-';
    },

    parseRemark: (_value, row) => {
        const attrs = getAttrs(row);
        const updates = attrs.channel_updates as Array<{
            channel_id: number;
            message: string | null;
            sync_status: number;
        }> | undefined;
        if (!Array.isArray(updates)) return '-';
        for (let i = 0; i < updates.length; i++) {
            if (updates[i].channel_id == 2) {
                const message = updates[i].message;
                if (message != null && message !== '') {
                    const isSuccess = [2, 5, 7, 12, 13].includes(updates[i].sync_status);
                    const iconClass = isSuccess ? 'icon-Success-grid-icon' : 'icon-Error-grid-icon';
                    return `
                        <div class='remark-tool-tip d-flex justify-content-start text-left'>
                            <span class="icon title-icon text-left ${iconClass} d-inline-block mr-1"></span>
                            <span class='d-inline-block text-item'> ${message}</span>
                        </div>
                    `;
                }
                return '-';
            }
        }
        return '-';
    },

    parsePOStatus: (value) => {
        if (!value) return '-';
        const lowerValue = String(value).toLowerCase();
        return `<div class="po-block"><div class="status-title"><span class="title-${lowerValue}">${value}</span></div></div>`;
    },

    parseType: (value) => {
        if (value == 2) return 'Offline';
        if (value == 1) return 'Online';
        return '-';
    },

    parseBulkAction: (value) => {
        if (!value) return '-';
        const str = String(value);
        let type = 'file';
        if (str.includes('upload') || str.includes('Upload')) type = 'upload';
        else if (str.includes('download')) type = 'download';
        return `<div class='bulk-action'><span class="icon icon-${type}"></span><span class='name'>${str}</span></div>`;
    },

    parseCount: (value) => `<span class="number-span">${value}<span>`,

    useroleMethod: (_value, row) => {
        const attrs = getAttrs(row);
        return attrs.user_role ? `<span class="role-span">${attrs.user_role}<span>` : '-';
    },

    ThreePartyLogisticsStatus: (_value, row) => {
        const attrs = getAttrs(row);
        if (!attrs) return '-';
        if (attrs.is_active) {
            return '<span class="icon icon-space icon-green-circle"></span><span class="ConnectedPlStaus">Connected</span>';
        }
        return '<span class="icon icon-space icon-red-circle"></span><span class="DisconnectedPlStaus">Disconnected</span>';
    },

    parseProductStockStatus: (_value, row) => {
        const attrs = getAttrs(row);
        if (attrs.product_stocks) {
            const statusClass = attrs.product_stocks_color ?? '';
            return `<div class="product-stock"><span class="stock-status-icon ${statusClass}"></span>${attrs.product_stocks}</div>`;
        }
        return `<div class="product-stock error"><span class="stock-status-icon error"></span>0</div>`;
    },

    productStock: (_value, row) => {
        const attrs = getAttrs(row);
        const data = attrs.product_stocks as Record<string, unknown> | undefined;
        if (!data) return '-';
        return `
<div class="batch-block">
  <div class="batch-block-first">
    <div class="batch-block-title batch-title2">
      <span class="icon icon-space icon-green-circle"></span>ATP
    </div>
    <div class="batch-block-value">${data.atp_stock != null ? data.atp_stock : '0'}</div>
  </div>
  <div class="batch-block-first">
    <div class="batch-block-title batch-title2">
      <span class="icon icon-space icon-green-circle"></span>Usable
    </div>
    <div class="batch-block-value">${data.total_usable_quantity != null ? data.total_usable_quantity : '-'}</div>
  </div>
  <div class="batch-block-first">
    <div class="batch-block-title batch-title3">
      <span class="icon icon-space icon-orange-circle"></span>In-Process
    </div>
    <div class="batch-block-value">${data.total_inprocess_quantity != null ? data.total_inprocess_quantity : '-'}</div>
  </div>
  <div class="batch-block-first">
    <div class="batch-block-title batch-title4">
      <span class="icon icon-space icon-red-circle"></span>Unusable
    </div>
    <div class="batch-block-value">${data.total_unusable_quantity != null ? data.total_unusable_quantity : '-'}</div>
  </div>
</div>`;
    },

    parsereportOccurence: (value) => {
        if (!value) return '-';
        const str = String(value);
        const lowerValue =
            str == 'Weekly' ? 'weekly'
            : str == 'Daily' ? 'pending'
            : str == 'Monthly' ? 'inprocess'
            : str == 'Quarterly' ? 'cancelled'
            : '';
        return `<div class="po-block"><div class="status-title"><span class="title-${lowerValue} fs-11">${str}</span></div></div>`;
    },

    parseReportStatus: (value) => {
        if (!value) return '-';
        const str = String(value);
        const statusClass =
            str == 'Completed' ? 'icon icon-pie-chart'
            : str == 'Deleted' ? 'icon icon-trash '
            : 'icon icon-book ';
        const lowerValue =
            str == 'Completed' ? 'success'
            : str == 'Deleted' ? 'cancelled'
            : 'archived';
        return `
            <div class="po-block">
            <div class="status-title">
              <span class="title-${lowerValue} ${statusClass} fs-11"><label class="pl-1"> ${str}
              </label>
             </span>
            </div>
          </div>`;
    },

    parseCarrierRuleStatus: (value) => {
        if (!value) return '-';
        const str = String(value);
        let icon: string;
        switch (str.toLowerCase()) {
            case 'in use':
                icon = '<span class="icon-green-circle"></span>';
                break;
            case 'not in use':
                icon = '<span class="icon-red-circle"></span>';
                break;
            default:
                icon = '<span class="icon-yellow-circle"></span>';
                break;
        }
        return icon + str;
    },

    parseSalesChannel: (_value, row) => {
        const attrs = getAttrs(row);
        const marketplace = attrs.seller_marketplace as { name?: string } | undefined;
        return marketplace?.name || '-';
    },

    parseShippingType: (value, row) => {
        const attrs = getAttrs(row);
        if (attrs.default) {
            if (!value) return '-';
            return `
                <div class="po-block d-flex justify-content-between align-items-center">
                    <span>${value}</span>
                    <div class="status-title">
                        <span class="title-success">Default</span>
                    </div>
                </div>`;
        }
        return value ? `<span>${value}</span>` : '-';
    },

    parseErrorType: (value) => {
        if (typeof localStorage === 'undefined') return value != null ? String(value) : '-';
        const isDpdLogistic = localStorage.getItem('isDpdLogistic');
        return isDpdLogistic ? 'Duplicate SKU' : (value != null ? String(value) : '-');
    },

    parseMultiWarehouseCarrier: (value) => (value ? String(value) : '-'),

    parseMarketplaceStatus: (value) => {
        if (!value) return '-';
        switch (String(value)) {
            case 'CONNECTED':
                return (
                    '<span class="icon icon-space icon-green-circle"></span>' +
                    '<span>Connected</span>'
                );
            case 'DISCONNECTED':
                return (
                    '<span class="icon icon-space icon-red-circle"></span>' +
                    '<span>Disconnected</span>'
                );
            default:
                return String(value);
        }
    },

    parseParcelOrderNumberCopy: (value) => renderCopyCell(value),

    parseParcelCodeCopy: (value) => renderCopyCell(value),

    parsePostCodeCopy: (value) => renderCopyCell(value),

    parseTrackingIDRedirect: (value, row) => {
        const attrs = getAttrs(row);
        const trackingId = attrs.tracking_id ?? value;
        const trackingUrl = attrs.tracking_url;
        if (trackingUrl && trackingId != null && trackingId !== '') {
            const id = escapeHtml(String(trackingId));
            const url = escapeAttr(String(trackingUrl));
            return (
                `<span class="anc-copy-cell">` +
                `<a href="${url}" target="_blank" class="linkText">${id}</a>` +
                `<a href="${url}" target="_blank"><span class="icon icon-Dispatch pl-2"></span></a>` +
                `</span>`
            );
        }
        return renderCopyCell(trackingId);
    },
};

/** Column keys that show a copy icon in anc-grid (legacy ngx-grid map rule). */
export const ANC_GRID_COPYABLE_KEYS = new Set(['order_number', 'tracking_number', 'cart_no']);

/** Render methods that embed their own copy control in cell HTML. */
export const ANC_GRID_COPY_RENDER_METHODS = new Set([
    'parseParcelOrderNumberCopy',
    'parseParcelCodeCopy',
    'parsePostCodeCopy',
    'parseTrackingIDRedirect',
]);

/** Render methods whose cells lay out multiple always-visible values (e.g. a flex badge row) rather than truncatable single-line text — skip the overflow tooltip since nothing is actually hidden. */
export const ANC_GRID_NO_TOOLTIP_RENDER_METHODS = new Set(['productStock']);

/**
 * Creates a cell renderer that dispatches to legacy ngx-grid method names.
 * Pass overrides to replace or add methods for a specific screen.
 */
export function createAncCellRenderer(
    overrides?: Record<string, AncCellRendererMethod>
): AncCellRendererFn {
    const methods = { ...DEFAULT_RENDERERS, ...overrides };
    return (value: unknown, method: string, row: unknown): string => {
        const fn = methods[method];
        if (fn) {
            const result = fn(value, row);
            if (result != null) return result;
        }
        return value != null && value !== '' ? String(value) : '-';
    };
}

/** Default renderer with all legacy ngx-grid string render methods. */
export const defaultAncCellRenderer = createAncCellRenderer();
